import {
    singleCreateMutation,
    singleRestoreMutation,
    singleUpdateMutation,
} from "api/graphqlTypes";
import { graphqlPost } from "api/graphqlClient";
import restClient, { retryRestClient } from "api/restClient";
import ExpandableNotification from "components/Notification/ExpandableNotification";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { showConfirmModal } from "components/Modal/showConfirmModal";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import {
    canLoadFileContentSelector,
    resourceDataSelector,
} from "containers/Resources/Resources.selectors";
import { ON_SAVE_MIGRATE_DEPRECATIONS_ENABLED } from "containers/Resources/screens/ResourcesSingle/useCanCommitOnSave";
import { getWorkspaceConfigValue } from "containers/WorkspaceConfig/WorkspaceConfig.selectors";
import { cloneDeep } from "lodash";
import { call, cancel, cancelled, fork, put, select, take, takeEvery, takeLatest } from "redux-saga/effects";
import { runBatchedListAction } from "containers/Resources/listActionSaga.utils";
import { checkContainerItemType, checkSubrecordType, getIri } from "utils/utils";

const getAsyncParams = (resourceName) => {
    const isContainer = checkContainerItemType(resourceName);
    const isCustomObject = resourceName === "custom_object";
    const recursive = isContainer;
    const wait = !isContainer && !isCustomObject;
    return {
        recursive,
        wait,
        isAsync: wait === false,
    };
};

function* simpleAction(successType, errorType, { promise, iri, params }) {
    try {
        yield graphqlPost(params);
        yield put({
            type: successType,
        });
        yield call(promise.resolve);
    } catch (error) {
        yield put({
            type: errorType,
            iri,
        });
        yield call(promise.reject, error?.response?.data || error);
        NotificationService.error(<RestErrorNotification error={error} />);
    }
}

/**
 * Delete Single Resource
 * @param  {Object} id ID of the resource
 * @return {Object} response from API
 */
export function* deleteResource({ promise, resourceName, id, name, parameters = {} }) {
    const isSubrecord = checkSubrecordType(resourceName);
    const defaultParams = yield getDefaultParams();
    const { recursive, wait, isAsync } = getAsyncParams(resourceName);
    try {
        yield (isAsync ? restClient : retryRestClient).delete(`/${resourceName}/${id}`, {
            params: {
                by: "id",
                commit: isSubrecord ? false : defaultParams.commit,
                migrate: defaultParams.migrate,
                recursive,
                wait,
                ...parameters,
            },
        });
        yield put({
            type: resourcesActions.DELETE_RESOURCE_SUCCESSFUL,
        });
        yield call(() => promise.resolve({ resourceName, id }));
    } catch (error) {
        yield put({
            type: resourcesActions.DELETE_RESOURCE_FAILED,
        });
        yield call(promise.reject, error?.response?.data || error);
        NotificationService.error(
            <RestErrorNotification
                titleOverride={`Sorry, couldn't move to trash ${name ?? ""}`}
                error={error}
            />
        );
    }
}

/**
 * Delete Multiple Resources
 * @params {Array} array of resources
 * @return {Array} array of responses from API
 */
export function* deleteResources({ promise, resources, parameters = {} }) {
    const defaultParams = yield getDefaultParams();

    const results = yield* runBatchedListAction(
        resources,
        "Moving to trash",
        function* processDeleteResource(res) {
            try {
                const isSubrecord = checkSubrecordType(res.resourceName);
                const { recursive, wait, isAsync } = getAsyncParams(res.resourceName);
                yield (isAsync ? restClient : retryRestClient).delete(
                    `/${res.resourceName}/${res.resourceData.id}`,
                    {
                        params: {
                            by: "id",
                            ...defaultParams,
                            commit: isSubrecord ? false : defaultParams.commit,
                            recursive,
                            wait,
                            ...parameters,
                        },
                    }
                );
                yield put({
                    type: resourcesActions.DELETE_RESOURCE_SUCCESSFUL,
                });

                return {
                    success: true,
                    resource: res,
                };
            } catch (error) {
                yield put({
                    type: resourcesActions.DELETE_RESOURCE_FAILED,
                });

                return {
                    success: false,
                    resource: res,
                    error: error?.response?.data || error,
                };
            }
        }
    );

    yield call(promise.resolve, results);
}

/**
 * Permanently delete resource
 * @param  {Object} id ID of the resource
 * @return {Object} response from API
 */
export function* deleteResourcePermanently({
    promise,
    resourceName,
    id,
    errorToastOverride,
    parameters = {},
}) {
    const defaultParams = yield getDefaultParams();
    const { recursive, wait, isAsync } = getAsyncParams(resourceName);
    try {
        yield (isAsync ? restClient : retryRestClient).delete(`/${resourceName}/${id}`, {
            params: {
                by: "id",
                permanently: true,
                allow_deleted: true,
                commit: defaultParams.commit,
                migrate: defaultParams.migrate,
                recursive,
                wait,
                ...parameters,
            },
        });
        yield put({
            type: resourcesActions.PERMANENTLY_DELETE_RESOURCE_SUCCESSFUL,
        });
        yield put({
            type: resourcesActions.CLEAR_RESOURCE_DATA,
            iri: `/${resourceName}/${id}`,
        });
        yield call(() => promise.resolve({ resourceName, id }));
    } catch (error) {
        yield put({
            type: resourcesActions.PERMANENTLY_DELETE_RESOURCE_FAILED,
        });
        yield call(promise.reject, error?.response?.data || error);
        NotificationService.error(
            <RestErrorNotification
                titleOverride={errorToastOverride ?? "Sorry, couldn't delete that permanently"}
                error={error}
            />
        );
    }
}

/**
 * Permanently Delete Multiple Resources
 * @params {Array} array of resources
 * @return {Array} array of responses from API
 */
export function* deleteResourcesPermanently({ promise, resources, parameters = {} }) {
    const defaultParams = yield getDefaultParams();

    const results = yield* runBatchedListAction(
        resources,
        "Deleting permanently",
        function* processPermanentlyDeleteResource(res) {
            try {
                const iri = `/${res.resourceName}/${res.resourceData.id}`;
                const { recursive, wait, isAsync } = getAsyncParams(res.resourceName);
                yield (isAsync ? restClient : retryRestClient).delete(iri, {
                    params: {
                        by: "id",
                        permanently: true,
                        allow_deleted: true,
                        ...defaultParams,
                        recursive,
                        wait,
                        ...parameters,
                    },
                });

                yield put({
                    type: resourcesActions.PERMANENTLY_DELETE_RESOURCE_SUCCESSFUL,
                });
                yield put({
                    type: resourcesActions.CLEAR_RESOURCE_DATA,
                    iri,
                });

                return {
                    success: true,
                    resource: res,
                };
            } catch (error) {
                yield put({
                    type: resourcesActions.PERMANENTLY_DELETE_RESOURCE_FAILED,
                });

                return {
                    success: false,
                    resource: res,
                    error: error?.response?.data || error,
                };
            }
        }
    );

    yield call(promise.resolve, results);
}

/**
 * Undelete/restore resource from trash
 * @param  {Object} id ID of the resource
 * @return {Object} response from API
 */
export function* undeleteResource({ promise, resourceName, id, parameters = {} }) {
    const defaultParams = yield getDefaultParams();
    const { recursive, isAsync } = getAsyncParams(resourceName);

    try {
        yield (isAsync ? restClient : retryRestClient).post(
            `/graphql`,
            {
                query: singleRestoreMutation(resourceName),
                variables: {
                    id,
                },
            },
            {
                params: {
                    allow_deleted: true,
                    recursive,
                    ...defaultParams,
                    ...parameters,
                },
            }
        );
        yield put({
            type: resourcesActions.UNDELETE_RESOURCE_SUCCESSFUL,
        });

        yield call(() => promise.resolve({ resourceName, id }));
    } catch (error) {
        yield put({
            type: resourcesActions.UNDELETE_RESOURCE_FAILED,
        });
        yield call(promise.reject, error?.response?.data || error);
        NotificationService.error(
            <RestErrorNotification titleOverride="Sorry, couldn't restore that" error={error} />
        );
    }
}

/**
 * Restore Multiple Resources from Trash
 * @params {Array} array of resources
 * @return {Array} array of responses from API
 */
export function* undeleteResources({ promise, resources, parameters = {} }) {
    const defaultParams = yield getDefaultParams();

    const results = yield* runBatchedListAction(
        resources,
        "Restoring",
        function* processUndeleteResource(res) {
            const { recursive, isAsync } = getAsyncParams(res.resourceName);
            try {
                yield (isAsync ? restClient : retryRestClient).post(
                    `/graphql`,
                    {
                        query: singleRestoreMutation(res.resourceName),
                        variables: {
                            id: res.resourceData.id,
                        },
                    },
                    {
                        params: {
                            allow_deleted: true,
                            recursive,
                            ...defaultParams,
                            ...parameters,
                        },
                    }
                );

                yield put({
                    type: resourcesActions.UNDELETE_RESOURCE_SUCCESSFUL,
                });

                return {
                    success: true,
                    resource: res,
                };
            } catch (error) {
                yield put({
                    type: resourcesActions.UNDELETE_RESOURCE_FAILED,
                });

                return {
                    success: false,
                    resource: res,
                    error: error?.response?.data || error,
                };
            }
        }
    );

    yield call(promise.resolve, results);
}

function* getCanCommitDefaultValue() {
    const canCommitDefaultSetting = yield select((state) =>
        getWorkspaceConfigValue(state, "GIT_AUTOCOMMIT_ENABLED")
    );
    return canCommitDefaultSetting === true;
}

function* getDefaultParams() {
    const canMigrateOnSaveDefaultValue = yield getMigrateOnSaveDefaultValue();
    const canCommitDefaultValue = yield getCanCommitDefaultValue();
    const defaultParams = {
        commit: canCommitDefaultValue,
        migrate: canMigrateOnSaveDefaultValue,
    };

    return defaultParams;
}

export function* migrateDeprecations({ promise, resourceName, id, name }) {
    try {
        const canCommitDefaultValue = yield getCanCommitDefaultValue();
        const response = yield retryRestClient.patch(
            `/${resourceName}/${id}/migrate_deprecations?by=id&commit=${canCommitDefaultValue}`
        );
        const partialUpdateData = cloneDeep(response.data);

        const iri = getIri(resourceName, id);
        const iriByName = getIri(resourceName, name);

        const resourceData = yield select(resourceDataSelector(iri));
        let resourceDataByName = null;
        if (!resourceData) {
            resourceDataByName = yield select(resourceDataSelector(iriByName));
        }

        if (resourceData || resourceDataByName) {
            yield put({
                type: resourcesActions.STORE_RESOURCE_DATA_WITH_PARTIAL_UPDATE,
                iri: resourceData ? iri : iriByName,
                response: partialUpdateData ?? {},
            });
        }

        if (response.data.errors?.length === 0) {
            NotificationService.success(`Deprecations migrated`);
        } else {
            if (response.data.errors?.length > 0) {
                NotificationService.info(
                    <ExpandableNotification
                        title="Sorry, couldn't migrate some deprecations"
                        details={
                            <ul>
                                {response.data.errors.map((error, i) => (
                                    <li key={i}>{error}</li>
                                ))}
                            </ul>
                        }
                        detailsForClipboard={JSON.stringify(response.data.errors)}
                    />
                );
            }
        }
        yield call(() => promise.resolve({ resourceName, id }));
    } catch (error) {
        yield call(promise.reject, error?.response?.data || error);
        NotificationService.error(
            <RestErrorNotification
                titleOverride="Sorry, couldn't migrate deprecations"
                error={error}
            />
        );
    }
}

export function* migrateDeprecationsResources({ promise, resources }) {
    const canCommitDefaultValue = yield getCanCommitDefaultValue();

    const results = yield* runBatchedListAction(
        resources,
        "Migrating deprecations",
        function* processMigrateDeprecationsResource(res) {
            try {
                const response = yield retryRestClient.patch(
                    `/${res.resourceName}/${res.resourceData.id}/migrate_deprecations?by=id&commit=${canCommitDefaultValue}`
                );

                if (response.data.errors?.length > 0) {
                    return {
                        resource: res,
                        success: true,
                        error: true,
                    };
                }

                return {
                    resource: res,
                    success: true,
                    error: false,
                };
            } catch (error) {
                return {
                    resource: res,
                    error: error?.response?.data || error,
                    success: false,
                };
            }
        }
    );

    yield call(promise.resolve, results);
}

/**
 * Create Resource
 * @param  {Object} formData
 * @return {Object} Response from API
 */
export function* createResource({
    promise,
    resourceName,
    variables,
    additionalParams,
    query,
    duplicateRecordId,
}) {
    const defaultParams = yield getDefaultParams();
    const action = duplicateRecordId ? "duplicate" : "create";
    try {
        const actionVariables = duplicateRecordId
            ? {
                  ...variables,
                  duplicateRecordId,
              }
            : variables;
        const createResourceAPI = yield retryRestClient.post(
            `/graphql`,
            {
                query: query ?? singleCreateMutation(resourceName, duplicateRecordId),
                variables: actionVariables,
            },
            { params: { ...defaultParams, ...(additionalParams ?? {}) } }
        );
        yield put({
            type: resourcesActions.CREATE_RESOURCE_SUCCESSFUL,
        });
        const resourceData = createResourceAPI?.data[`${action}_${resourceName}`];
        yield put({
            type: resourcesActions.STORE_RESOURCE_DATA,
            iri: `/${resourceName}/${resourceData?.id}`,
            response: resourceData,
        });
        yield call(promise.resolve, resourceData);
    } catch (error) {
        yield put({
            type: resourcesActions.CREATE_RESOURCE_FAILED,
        });
        yield call(promise.reject, error?.response?.data || error);
        NotificationService.error(
            <RestErrorNotification
                titleOverride={`Sorry, couldn't ${action} ${resourceName}`}
                error={error}
            />
        );
    }
}

export function* importResource({ promise, fileData, fileName }) {
    const defaultParams = yield getDefaultParams();
    try {
        yield retryRestClient.post(`/import?file_name=${fileName}&async=true`, fileData, {
            headers: { "Content-Type": "application/octet-stream" },
            maxBodyLength: Infinity,
            maxContentLength: Infinity,
            params: defaultParams,
        });
        NotificationService.info("Import started");
        yield call(promise.resolve);
    } catch (error) {
        yield call(promise.reject, error?.response?.data || error);
        NotificationService.error(
            <RestErrorNotification titleOverride="Sorry, import failed" error={error} />
        );
    }
}

function* getMigrateOnSaveDefaultValue() {
    const isMigrateDeprecationsOnSaveEnabled = yield select((state) =>
        getWorkspaceConfigValue(state, ON_SAVE_MIGRATE_DEPRECATIONS_ENABLED)
    );

    return isMigrateDeprecationsOnSaveEnabled;
}

/**
 * Optimistic-concurrency stale-write resolution dialog. Shown when a guarded
 * update (one that sent `expected_version`) is rejected with HTTP 409 because the
 * record was modified by someone else after it was loaded. Non-destructive: the
 * safe action (Reload — adopt the server's current record) is the default (also on
 * ESC/close); overwriting the other person's changes is an explicit danger-styled
 * choice. Resolves to "overwrite" or "reload".
 */
const promptStaleWriteResolution = ({ current }) =>
    new Promise((resolve) => {
        let settled = false;
        const settle = (choice) => {
            if (!settled) {
                settled = true;
                resolve(choice);
            }
        };
        showConfirmModal({
            title: "This record changed",
            content: (
                <span>
                    {current?.name ? <b>{current.name}</b> : "This record"} was modified by
                    someone else after you opened it. Reload to discard your changes and load
                    theirs, or overwrite their changes with your version.
                </span>
            ),
            confirmText: "Overwrite their changes",
            cancelText: "Reload",
            confirmButtonProps: { danger: true },
            onConfirm: () => settle("overwrite"),
            onCancel: () => settle("reload"),
            className: "stale-write-conflict-modal",
        });
    });

export function* updateResource({
    promise,
    resourceName,
    variables,
    callback = null,
    additionalQueryFields,
    additionalParams,
    query,
    iri: actionIri,
}) {
    const defaultParams = yield getDefaultParams();
    const params = { ...defaultParams, ...(additionalParams ?? {}) };
    let currentVariables = variables;

    // Retry loop: a guarded write can 409 (stale write); on "overwrite" we retry
    // once with the fresh token from the conflict body. Every other outcome returns.
    // eslint-disable-next-line no-constant-condition
    while (true) {
        try {
            const hasExpectedVersion = currentVariables?.expected_version != null;
            const updateResourceAPI = yield retryRestClient.post(
                `/graphql`,
                {
                    query:
                        query ??
                        singleUpdateMutation(
                            resourceName,
                            additionalQueryFields,
                            hasExpectedVersion
                        ),
                    variables: currentVariables,
                },
                { params }
            );
            yield put({
                type: resourcesActions.UPDATE_RESOURCE_SUCCESSFUL,
            });
            const resourceData = updateResourceAPI?.data[`update_${resourceName}`];
            const iri = actionIri ?? getIri(resourceName, resourceData?.id);
            const canLoadFileContent = yield select(canLoadFileContentSelector(iri));
            let content = undefined;

            if (canLoadFileContent) {
                const fetchFileContentApi = yield graphqlPost({
                    query: `query ($by: String, $id: String!) {
                        ${resourceName}(by: $by, id: $id) {
                            content
                        }
                    }
                    `,
                    variables: {
                        id: resourceData?.id,
                    },
                    allow_deleted: true,
                });
                content = fetchFileContentApi?.data?.file?.content;
            }
            yield put({
                type: resourcesActions.STORE_RESOURCE_DATA,
                iri,
                response: content ? { ...resourceData, content } : resourceData,
            });
            yield call(promise.resolve, resourceData);
            if (callback) {
                yield callback(currentVariables);
            }
            return;
        } catch (error) {
            // Optimistic-concurrency stale-write conflict: offer a non-destructive
            // reload / overwrite choice instead of silently failing/clobbering.
            if (
                error?.response?.status === 409 &&
                currentVariables?.expected_version != null
            ) {
                const conflict = error?.response?.data ?? {};
                const choice = yield call(promptStaleWriteResolution, {
                    current: conflict.current,
                });
                if (choice === "overwrite" && conflict.current_version) {
                    currentVariables = {
                        ...currentVariables,
                        expected_version: conflict.current_version,
                    };
                    continue; // retry, forcing over the fresh token
                }
                // Reload: adopt the server's current record and unwind the save.
                yield put({
                    type: resourcesActions.UPDATE_RESOURCE_FAILED,
                });
                if (conflict.current) {
                    const iri = actionIri ?? getIri(resourceName, conflict.current.id);
                    yield put({
                        type: resourcesActions.STORE_RESOURCE_DATA,
                        iri,
                        response: conflict.current,
                    });
                }
                yield call(promise.reject, conflict);
                return;
            }
            yield put({
                type: resourcesActions.UPDATE_RESOURCE_FAILED,
            });
            yield call(promise.reject, error?.response?.data || error);
            NotificationService.error(
                <RestErrorNotification titleOverride="Sorry, update failed" error={error} />
            );
            return;
        }
    }
}

export function* updateResources({ promise, resources, variables }) {
    const defaultParams = yield getDefaultParams();

    const results = yield* runBatchedListAction(
        resources,
        "Updating",
        function* processUpdateResource(res) {
            try {
                const updateResourceAPI = yield retryRestClient.post(
                    `/graphql`,
                    {
                        query: singleUpdateMutation(res.resourceName),
                        variables: {
                            id: res.resourceData.id,
                            ...variables,
                        },
                    },
                    { params: defaultParams }
                );

                yield put({
                    type: resourcesActions.UPDATE_RESOURCE_SUCCESSFUL,
                });

                const resourceData = updateResourceAPI?.data[`update_${res.resourceName}`];
                yield put({
                    type: resourcesActions.STORE_RESOURCE_DATA,
                    iri: `/${res.resourceName}/${resourceData?.id}`,
                    response: resourceData,
                });

                return {
                    success: true,
                    resource: res,
                };
            } catch (error) {
                yield put({
                    type: resourcesActions.UPDATE_RESOURCE_FAILED,
                });
                return {
                    success: false,
                    resource: res,
                    error: error?.response?.data || error,
                };
            }
        }
    );

    yield call(promise.resolve, results);
}

/**
 * Fetch Resource
 * @param  {Object} promise
 * @param {string} id Resource ID
 * @return {Object} response from API
 */
export function* fetchResource({ promise, iri, params, resourceName }) {
    try {
        const fetchResourceAPI = yield graphqlPost(params);
        yield put({
            type: resourcesActions.FETCH_RESOURCE_SUCCESSFUL,
            iri,
        });
        yield put({
            type: resourcesActions.STORE_RESOURCE_DATA,
            iri,
            response: fetchResourceAPI?.data[resourceName],
        });
        yield call(promise.resolve, fetchResourceAPI?.data[resourceName]);
    } catch (error) {
        if (yield cancelled()) {
            yield put({
                type: resourcesActions.FETCH_RESOURCE_FAILED,
                iri,
            });
            return;
        }
        yield put({
            type: resourcesActions.FETCH_RESOURCE_FAILED,
            iri,
        });
        if (error?.response?.status === 404) {
            yield put({
                type: resourcesActions.CLEAR_RESOURCE_DATA,
                iri,
            });
        }
        yield call(promise.reject, error?.response?.data || error);
        NotificationService.error(
            <RestErrorNotification titleOverride="Sorry, couldn't get resource" error={error} />
        );
    }
}

function takeLatestPerKey(pattern, keySelector, worker) {
    return fork(function* () {
        const tasks = {};
        while (true) {
            const action = yield take(pattern);
            const key = keySelector(action);
            if (tasks[key]) {
                yield cancel(tasks[key]);
            }
            tasks[key] = yield fork(worker, action);
        }
    });
}

/**
 * Close Resource
 * @param  {Object} promise
 * @param {string} id Resource ID
 * @return {Object} response from API
 */
export function* closeResource({ promise, params }) {
    yield* simpleAction(
        resourcesActions.CLOSE_RESOURCE_SUCCESSFUL,
        resourcesActions.CLOSE_RESOURCE_FAILED,
        { promise, params }
    );
}

/**
 * Pin Resource
 * @param  {Object} promise
 * @param {string} id Resource ID
 * @return {Object} response from API
 */
export function* pinResource({ promise, params }) {
    yield* simpleAction(
        resourcesActions.PIN_RESOURCE_SUCCESSFUL,
        resourcesActions.PIN_RESOURCE_FAILED,
        { promise, params }
    );
}

/**
 * Unpin Resource
 * @param  {Object} promise
 * @param {string} id Resource ID
 * @return {Object} response from API
 */
export function* unpinResource({ promise, params }) {
    yield* simpleAction(
        resourcesActions.UNPIN_RESOURCE_SUCCESSFUL,
        resourcesActions.UNPIN_RESOURCE_FAILED,
        { promise, params }
    );
}

/**
 * Fetch Resource
 * @param  {Object} promise
 * @param {string} id Resource ID
 * @return {Object} response from API
 */
export function* fetchVisualization({ promise, id, recordType, by }) {
    try {
        const response = yield restClient.get(`/${recordType}/${id}/visualization`, {
            params: { allow_deleted: true, by },
        });
        yield put({
            type: resourcesActions.FETCH_VISUALIZATION_SUCCESSFUL,
            id,
        });
        yield put({
            type: resourcesActions.STORE_RESOURCE_DATA,
            iri: `/${recordType}/${id}/visualization`,
            response: response?.data,
        });
        yield call(promise.resolve, response?.data);
    } catch (error) {
        yield put({
            type: resourcesActions.FETCH_VISUALIZATION_FAILED,
            id,
        });
        yield call(promise.reject, error?.response?.data || error);
    }
}

/**
 * Register action to watcher
 */
export default [
    takeEvery(resourcesActions.START_DELETE_RESOURCE, deleteResource),
    takeEvery(resourcesActions.START_DELETE_RESOURCES, deleteResources),
    takeEvery(resourcesActions.START_PERMANENTLY_DELETE_RESOURCE, deleteResourcePermanently),
    takeEvery(resourcesActions.START_PERMANENTLY_DELETE_RESOURCES, deleteResourcesPermanently),
    takeEvery(resourcesActions.START_UNDELETE_RESOURCE, undeleteResource),
    takeEvery(resourcesActions.START_UNDELETE_RESOURCES, undeleteResources),
    takeEvery(resourcesActions.START_MIGRATE_DEPRECATIONS, migrateDeprecations),
    takeEvery(resourcesActions.START_MIGRATE_DEPRECATIONS_RESOURCES, migrateDeprecationsResources),
    takeEvery(resourcesActions.START_CREATE_RESOURCE, createResource),
    takeEvery(resourcesActions.START_IMPORT_RESOURCE, importResource),
    takeLatestPerKey(resourcesActions.START_FETCHING_RESOURCE, (action) => action.iri, fetchResource),
    takeEvery(resourcesActions.START_UPDATE_RESOURCE, updateResource),
    takeEvery(resourcesActions.START_UPDATE_RESOURCES, updateResources),
    takeLatest(resourcesActions.START_CLOSING_RESOURCE, closeResource),
    takeLatest(resourcesActions.START_PINNING_RESOURCE, pinResource),
    takeLatest(resourcesActions.START_UNPINNING_RESOURCE, unpinResource),
    takeEvery(resourcesActions.START_FETCHING_VISUALIZATION, fetchVisualization),
];
